import { dataUrlToBytes } from './screenshot'

export interface CaptureOptions {
  /** A PNG data URL of the 3D viewport, rendered on demand. See below. */
  snapshot3D?: string | null
}

export type Capturer = (
  element: HTMLElement,
  options?: CaptureOptions,
) => Promise<HTMLCanvasElement>

export interface CaptureDeps {
  capture: Capturer
  target: () => HTMLElement | null
}

/**
 * How the 3D view hands over an image of itself.
 *
 * A WebGL drawing buffer is cleared once the frame is composited, so anything
 * reading that canvas afterwards — including a DOM capturer, which draws
 * canvases with `drawImage` — gets a blank rectangle. The usual fix is
 * `preserveDrawingBuffer: true` on the renderer, which keeps the buffer around
 * for every frame of every session to serve a feature almost nobody uses.
 *
 * Instead the 3D view registers a function that renders once and reads the
 * result synchronously, before yielding to the compositor. That costs nothing
 * until a bundle is actually captured. It is registered rather than imported
 * because the renderer lives inside the react-three-fiber tree, several lazy
 * chunks away from here.
 */
let snapshot3D: (() => string) | null = null

export function register3DSnapshot(fn: (() => string) | null): void {
  snapshot3D = fn
}

function read3D(): string | null {
  if (!snapshot3D) return null
  try {
    return snapshot3D()
  } catch {
    // A lost WebGL context must cost the 3D layer, never the whole screenshot.
    return null
  }
}

/**
 * Captures the visible app as a PNG.
 *
 * Returns `undefined` on any failure rather than throwing or substituting a
 * placeholder: an absent part paired with the artifact state is what keeps
 * "capture failed" distinguishable from "the user opted out", and a zero-byte
 * image would collapse the two.
 */
export async function captureViewport(deps: CaptureDeps): Promise<Uint8Array | undefined> {
  const element = deps.target()
  if (!element) return undefined

  try {
    const canvas = await deps.capture(element, { snapshot3D: read3D() })
    return dataUrlToBytes(canvas.toDataURL('image/png'))
  } catch {
    return undefined
  }
}
